import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Video, Camera, Maximize2, Minimize2, RefreshCw, Eye, Crosshair, Thermometer, CircleDot, MapPin, Gauge, Battery, Navigation, Wind, Satellite, Compass, Play, Square, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import BytePlusRTC, { StreamIndex } from '@byteplus/rtc';

const CAMERAS = [
  { id: 'wide', label: 'WIDE', sublabel: 'Grande Angular', icon: Eye, color: 'text-blue-500', bg: 'bg-blue-500', bgLight: 'bg-blue-50 border-blue-200' },
  { id: 'tele', label: 'ZOOM', sublabel: 'Teleobjetiva', icon: Crosshair, color: 'text-emerald-500', bg: 'bg-emerald-500', bgLight: 'bg-emerald-50 border-emerald-200' },
  { id: 'thermal', label: 'THERMAL', sublabel: 'Infravermelho', icon: Thermometer, color: 'text-orange-500', bg: 'bg-orange-500', bgLight: 'bg-orange-50 border-orange-200' },
  { id: 'dock', label: 'DOCK CAM', sublabel: 'Camera do Dock', icon: Camera, color: 'text-gray-500', bg: 'bg-gray-500', bgLight: 'bg-gray-50 border-gray-200' },
];

const FLIGHT_MODES = {
  0: 'Idle', 1: 'Takeoff', 2: 'Landing', 3: 'Motor Start',
  4: 'Landing', 5: 'Hover', 6: 'Mission', 7: 'RTH',
  8: 'Ascending', 9: 'Orbit', 14: 'Wayline', 15: 'Emergency',
  'N/A': 'Sem dados',
};

function parseVolcUrl(urlStr) {
  const params = new URLSearchParams(urlStr);
  return {
    appId: params.get('app_id') || '',
    roomId: params.get('room_id') || '',
    token: params.get('token') || '',
    userId: params.get('user_id') || '',
  };
}

const droneIcon = new L.DivIcon({
  html: `<div style="width:32px;height:32px;display:flex;align-items:center;justify-content:center;background:#1f2a36;border:2px solid #00ccff;border-radius:50%;box-shadow:0 0 12px rgba(0,204,255,0.5)"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00ccff" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></div>`,
  className: '', iconSize: [32, 32], iconAnchor: [16, 16],
});

const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => { if (center[0] !== 0 && center[1] !== 0) map.setView(center, map.getZoom(), { animate: true }); }, [center, map]);
  return null;
};

const TelemetryCard = ({ icon: Icon, label, value, unit, color = 'text-navy', warning }) => (
  <div className={`flex items-center gap-3 p-3 rounded-lg border ${warning ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`} data-testid={`telem-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${warning ? 'bg-red-100' : 'bg-gray-50'}`}>
      <Icon size={16} className={warning ? 'text-red-500' : 'text-gold'} />
    </div>
    <div className="min-w-0">
      <p className="text-gray-400 text-[10px] font-mono uppercase tracking-wider">{label}</p>
      <p className={`font-rajdhani font-bold text-sm ${color}`}>{value}<span className="text-gray-400 font-normal text-xs ml-1">{unit}</span></p>
    </div>
  </div>
);

const LivePage = () => {
  const rtcEngineRef = useRef(null);
  const playerRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [streamActive, setStreamActive] = useState(false);
  const [streamLoading, setStreamLoading] = useState(false);
  const [activeCamera, setActiveCamera] = useState('wide');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [djiStatus, setDjiStatus] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const containerRef = useRef(null);

  // Volcengine RTC connection
  const connectVolc = useCallback(async (volcUrl) => {
    const volcParams = parseVolcUrl(volcUrl);
    if (!volcParams.appId || !volcParams.roomId || !volcParams.token) {
      console.error('Volc RTC: Invalid params');
      return;
    }
    setConnecting(true);
    setConnected(false);
    try {
      if (rtcEngineRef.current) {
        try { rtcEngineRef.current.leaveRoom(); rtcEngineRef.current.destroy(); } catch {}
        rtcEngineRef.current = null;
      }
      const engine = BytePlusRTC.createEngine(volcParams.appId);
      rtcEngineRef.current = engine;

      const bindRemoteVideo = (uid) => {
        if (playerRef.current && uid) {
          engine.setRemoteVideoPlayer(StreamIndex.STREAM_INDEX_MAIN, {
            userId: uid, renderDom: playerRef.current,
          });
          setConnected(true);
          setConnecting(false);
        }
      };

      engine.on(BytePlusRTC.events.onAutoSubscribeResult, (event) => {
        if (event.state === 0 && !event.isScreen) bindRemoteVideo(event.userId);
      });
      engine.on(BytePlusRTC.events.onUserPublishStream, (event) => {
        if (!event.mediaType || event.mediaType === 1) bindRemoteVideo(event.userId);
      });
      engine.on(BytePlusRTC.events.onConnectionStateChanged, (event) => {
        console.log('Volc RTC state:', event);
      });

      // joinRoom already handles auto-subscribe via options
      await engine.joinRoom(volcParams.token, volcParams.roomId, { userId: volcParams.userId }, { isAutoPublish: false, isAutoSubscribeAudio: true, isAutoSubscribeVideo: true });
    } catch (err) {
      console.error('Volc RTC error:', err);
      toast.error(`Erro RTC: ${err.message || 'conexao falhou'}`);
      setConnecting(false);
    }
  }, []);

  const disconnectVolc = useCallback(() => {
    if (rtcEngineRef.current) {
      try { rtcEngineRef.current.leaveRoom(); rtcEngineRef.current.destroy(); } catch {}
      rtcEngineRef.current = null;
    }
    setConnected(false);
    setConnecting(false);
  }, []);

  // Fetch DJI status
  useEffect(() => {
    const fetchStatus = async () => {
      try { const res = await axios.get('/api/live/dji/status'); setDjiStatus(res.data); } catch {}
    };
    fetchStatus();
    const i = setInterval(fetchStatus, 15000);
    return () => clearInterval(i);
  }, []);

  // Telemetry polling (FH2 REST API - low frequency to avoid interference)
  useEffect(() => {
    const fetchTelemetry = async () => {
      try { const res = await axios.get('/api/live/telemetry'); setTelemetry(res.data); } catch {}
    };
    fetchTelemetry();
    const i = setInterval(fetchTelemetry, 15000);
    return () => clearInterval(i);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => disconnectVolc(), [disconnectVolc]);

  // START STREAM (with mission guard)
  const startStreamRef = useRef(false);
  const startStream = async (force = false) => {
    if (startStreamRef.current || streamLoading) return;
    startStreamRef.current = true;
    setStreamLoading(true);
    try {
      const res = await axios.post('/api/live/dji/stream/start', { camera: activeCamera, force });
      setStreamActive(true);
      const volcUrl = res.data.volc_url;
      if (volcUrl) {
        toast.success('Stream iniciado via FlightHub 2!');
        connectVolc(volcUrl);
      } else {
        toast.warning('Stream iniciado mas sem URL Volcengine');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 409) {
        // Mission active - show warning with force option
        toast.error(
          <div>
            <p className="font-bold mb-1">Drone em missao ativa!</p>
            <p className="text-xs mb-2">Iniciar o stream vai interromper a missao e causar RTH.</p>
            <button onClick={() => { toast.dismiss(); startStream(true); }} className="bg-red-600 text-white text-xs px-3 py-1 rounded hover:bg-red-700">
              FORCAR INICIO (RISCO RTH)
            </button>
          </div>,
          { duration: 15000 }
        );
      } else {
        toast.error(detail || 'Erro ao iniciar stream');
      }
    } finally {
      setStreamLoading(false);
      setTimeout(() => { startStreamRef.current = false; }, 3000);
    }
  };

  // STOP STREAM
  const stopStream = async () => {
    setStreamLoading(true);
    try {
      await axios.post('/api/live/dji/stream/stop');
      setStreamActive(false);
      disconnectVolc();
      toast.info('Stream parado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao parar stream');
    } finally { setStreamLoading(false); }
  };

  // SWITCH CAMERA
  const DRONE_CAMERAS = ['wide', 'tele', 'thermal'];
  const switchCamera = async (cameraId) => {
    if (switching || cameraId === activeCamera) return;
    setSwitching(true);
    const prevCamera = activeCamera;
    const isDroneLensSwitch = DRONE_CAMERAS.includes(prevCamera) && DRONE_CAMERAS.includes(cameraId);
    
    try {
      if (isDroneLensSwitch && streamActive) {
        // Same drone camera, just change lens - NO stream restart needed
        await axios.post('/api/live/dji/camera/switch', { camera: cameraId });
        setActiveCamera(cameraId);
        toast.success(`Lente: ${cameraId.toUpperCase()}`);
      } else if (streamActive) {
        // Different camera (drone ↔ dock) - need full stream restart
        disconnectVolc();
        try { await axios.post('/api/live/dji/camera/switch', { camera: cameraId }); } catch {}
        setActiveCamera(cameraId);
        const res = await axios.post('/api/live/dji/stream/start', { camera: cameraId });
        if (res.data.volc_url) connectVolc(res.data.volc_url);
        toast.success(`Camera: ${cameraId.toUpperCase()}`);
      } else {
        // Stream not active, just update selection
        setActiveCamera(cameraId);
        toast.success(`Camera: ${cameraId.toUpperCase()}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao trocar camera');
    } finally { setSwitching(false); }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) { document.exitFullscreen(); setIsFullscreen(false); }
    else { containerRef.current.requestFullscreen(); setIsFullscreen(true); }
  };

  const activeCam = CAMERAS.find(c => c.id === activeCamera) || CAMERAS[0];
  const hasPosition = telemetry && telemetry.latitude !== 0 && telemetry.longitude !== 0;
  const mapCenter = hasPosition ? [telemetry.latitude, telemetry.longitude] : [-10.96, -38.13];
  const flightModeLabel = telemetry ? (FLIGHT_MODES[telemetry.flight_mode] || FLIGHT_MODES[telemetry.drone_mode_code] || `Mode ${telemetry.flight_mode}`) : 'Sem dados';
  const lowBattery = telemetry?.battery > 0 && telemetry.battery < 20;
  const droneInMission = telemetry?.drone_online && [6, 14].includes(telemetry?.drone_mode_code);

  return (
    <div className="space-y-4" data-testid="live-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Video size={22} className="text-gold" />
          <h2 className="font-rajdhani font-semibold text-base uppercase tracking-wider text-navy" data-testid="stream-title">
            LIVE - MATRICE 3TD / DOCK 2
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {telemetry?.drone_online !== undefined && (
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${telemetry.drone_online ? 'bg-green-50 text-green-600 border-green-200' : 'bg-red-50 text-red-500 border-red-200'}`} data-testid="drone-status-badge">
              DRONE: {telemetry.drone_online ? 'ONLINE' : 'OFFLINE'}
            </span>
          )}
          <span className="text-[10px] font-mono text-gray-400" data-testid="system-status">SISTEMA ATIVO</span>
        </div>
      </div>

      {/* Mission warning banner */}
      {droneInMission && !streamActive && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="mission-warning">
          <AlertTriangle size={18} className="text-amber-500 flex-shrink-0" />
          <div>
            <p className="text-amber-800 font-semibold text-sm">Drone em missao ativa (Mode: {flightModeLabel})</p>
            <p className="text-amber-600 text-xs">Iniciar livestream via FH2 vai interromper a missao e causar RTH. Aguarde a missao terminar.</p>
          </div>
        </div>
      )}

      {/* Controls bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-gray-500">VOLCENGINE RTC</span>
        </div>
        <div className="flex items-center gap-2">
          {!streamActive ? (
            <Button onClick={() => startStream()} disabled={streamLoading || !djiStatus?.fh2_configured} className="bg-green-600 hover:bg-green-700 text-white font-mono text-xs h-9" data-testid="start-stream-btn">
              {streamLoading ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Play size={14} className="mr-2" />}
              INICIAR STREAM
            </Button>
          ) : (
            <Button onClick={stopStream} disabled={streamLoading} className="bg-red-600 hover:bg-red-700 text-white font-mono text-xs h-9" data-testid="stop-stream-btn">
              <Square size={14} className="mr-2" /> PARAR STREAM
            </Button>
          )}
          <Button variant="outline" onClick={toggleFullscreen} disabled={!streamActive} className="font-mono text-xs h-9" data-testid="fullscreen-btn-top">
            <Maximize2 size={14} />
          </Button>
          {connected && <span className="text-green-500 text-xs font-mono animate-pulse" data-testid="volc-connected">RTC CONECTADO</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Video player */}
        <div ref={containerRef} className="relative rounded-xl overflow-hidden shadow-lg bg-[#0a1628] border border-gray-200">
          <div className="relative" style={{ paddingTop: '56.25%' }}>
            {/* Volcengine RTC player */}
            <div ref={playerRef} id="volc-player-container" data-testid="volc-player"
              className="absolute inset-0 w-full h-full bg-black"
              style={{ zIndex: 1 }}
              onClick={() => {
                const videos = playerRef.current?.querySelectorAll('video');
                if (videos) videos.forEach(v => v.play().catch(() => {}));
              }}
            />

            {/* Offline overlay */}
            {!streamActive && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0a1628] z-10">
                <Video size={40} className="text-white/30 mb-4" />
                <p className="text-white/50 font-mono text-sm mb-2" data-testid="offline-label">STREAM OFFLINE</p>
                <p className="text-white/30 font-mono text-xs mb-4">Clique INICIAR STREAM para conectar via FlightHub 2</p>
                <Button onClick={() => startStream()} disabled={streamLoading || !djiStatus?.fh2_configured} className="bg-green-600 hover:bg-green-700 text-white font-mono text-xs" data-testid="start-stream-overlay-btn">
                  <Play size={14} className="mr-2" /> INICIAR STREAM
                </Button>
              </div>
            )}

            {/* Connecting overlay */}
            {streamActive && !connected && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0a1628]/80 z-10">
                <RefreshCw size={40} className="text-gold animate-spin mb-4" />
                <p className="text-white/70 font-mono text-sm" data-testid="connecting-label">CONECTANDO RTC...</p>
              </div>
            )}

            {/* Top overlay */}
            {streamActive && connected && (
              <div className="absolute top-0 left-0 right-0 p-3 flex items-center justify-between bg-gradient-to-b from-black/60 to-transparent z-20 pointer-events-none">
                <div className="flex items-center gap-2">
                  <CircleDot size={10} className="text-red-500 animate-pulse" />
                  <span className="text-white/90 font-mono text-[11px] tracking-wider">LIVE</span>
                  <span className={`font-mono text-[11px] tracking-wider px-2 py-0.5 rounded ${activeCam.bg} text-white`}>{activeCam.label}</span>
                  <span className="text-white/40 font-mono text-[9px] uppercase">VOLCENGINE RTC</span>
                </div>
                {telemetry?.battery > 0 && <span className="text-white/50 font-mono text-[10px]">BAT {telemetry.battery}% | ALT {telemetry.altitude?.toFixed?.(1) || 0}m</span>}
              </div>
            )}

            {/* Bottom controls overlay */}
            {streamActive && connected && (
              <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between bg-gradient-to-t from-black/60 to-transparent z-20">
                <div className="flex gap-1.5">
                  {CAMERAS.map(cam => (
                    <button key={cam.id} onClick={() => switchCamera(cam.id)} disabled={switching}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-mono tracking-wider transition-all ${activeCamera === cam.id ? `${cam.bg} text-white shadow-lg` : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                      data-testid={`cam-btn-${cam.id}`}>
                      <cam.icon size={10} /> {cam.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={toggleFullscreen} className="w-8 h-8 rounded-md bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70" data-testid="fullscreen-btn">
                    {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar: Map + Telemetry */}
        <div className="space-y-4">
          {/* Map */}
          <div className="rounded-xl overflow-hidden shadow-sm border border-gray-200" style={{ height: '180px' }} data-testid="mini-map">
            <MapContainer center={mapCenter} zoom={15} style={{ height: '100%', width: '100%' }} zoomControl={false}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="OSM" />
              <MapUpdater center={mapCenter} />
              {hasPosition && (
                <Marker position={[telemetry.latitude, telemetry.longitude]} icon={droneIcon}>
                  <Popup>Matrice 3TD - Alt: {telemetry.altitude?.toFixed?.(1)}m</Popup>
                </Marker>
              )}
            </MapContainer>
          </div>

          {/* Telemetry */}
          <div className="card-hud p-3" data-testid="telemetry-panel">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-navy font-rajdhani font-semibold text-xs uppercase tracking-wider flex items-center gap-1.5">
                <Gauge size={12} className="text-gold" /> TELEMETRIA
              </h3>
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-full ${telemetry?.source === 'fh2_rest' ? 'bg-blue-50 text-blue-500 border border-blue-200' : telemetry ? 'bg-green-50 text-green-500 border border-green-200' : 'bg-gray-100 text-gray-400 border border-gray-200'}`} data-testid="telemetry-source">
                {telemetry?.source === 'fh2_rest' ? 'FH2 REST' : telemetry ? 'MQTT OSD' : 'AGUARDANDO'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <TelemetryCard icon={MapPin} label="Latitude" value={telemetry?.latitude?.toFixed?.(6) || '--'} unit="" />
              <TelemetryCard icon={MapPin} label="Longitude" value={telemetry?.longitude?.toFixed?.(6) || '--'} unit="" />
              <TelemetryCard icon={Navigation} label="Altitude" value={telemetry?.altitude?.toFixed?.(1) || '--'} unit="m" />
              <TelemetryCard icon={Gauge} label="Velocidade" value={telemetry?.speed?.toFixed?.(1) || '--'} unit="m/s" />
              <TelemetryCard icon={Battery} label="Bateria" value={telemetry?.battery || '--'} unit="%" warning={lowBattery} color={lowBattery ? 'text-red-600' : 'text-navy'} />
              <TelemetryCard icon={Compass} label="Heading" value={telemetry?.heading?.toFixed?.(0) || '--'} unit={'\u00B0'} />
              <TelemetryCard icon={Satellite} label="Satelites" value={telemetry?.satellites || '--'} unit="" />
              <TelemetryCard icon={Wind} label="Vento" value={telemetry?.wind_speed?.toFixed?.(1) || '--'} unit="m/s" />
            </div>
            {/* Flight mode */}
            <div className="mt-2 flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-100" data-testid="flight-mode-display">
              <span className="text-[9px] font-mono text-gray-400 uppercase">Modo de voo</span>
              <span className="text-xs font-mono font-semibold text-navy">{flightModeLabel}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Camera cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {CAMERAS.map(cam => {
          const isActive = cam.id === activeCamera;
          return (
            <button key={cam.id} onClick={() => switchCamera(cam.id)} disabled={switching}
              className={`card-hud p-4 transition-all cursor-pointer text-left ${isActive ? cam.bgLight + ' border' : 'hover:shadow-md'}`}
              data-testid={`camera-card-${cam.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className={`w-10 h-10 rounded-lg ${isActive ? cam.bg + '/10' : 'bg-gray-100'} flex items-center justify-center`}>
                  <cam.icon size={20} className={isActive ? cam.color : 'text-gray-400'} />
                </div>
                {isActive && <span className="text-[10px] font-mono tracking-wider px-2 py-0.5 rounded-full bg-green-100 text-green-600 border border-green-200">ATIVA</span>}
              </div>
              <p className={`font-rajdhani font-semibold text-sm uppercase tracking-wider ${isActive ? 'text-navy' : 'text-gray-600'}`}>{cam.label}</p>
              <p className="text-gray-400 text-xs font-mono">{cam.sublabel}</p>
            </button>
          );
        })}
        {/* DJI Status card */}
        <div className="card-hud p-4" data-testid="dji-cloud-card">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center"><Camera size={20} className="text-gold" /></div>
            <span className={`text-[10px] font-mono tracking-wider px-2 py-0.5 rounded-full border ${djiStatus?.fh2_configured ? 'bg-green-50 text-green-600 border-green-200' : 'bg-yellow-50 text-yellow-600 border-yellow-200'}`}>
              {djiStatus?.fh2_configured ? 'CONECTADO' : 'PENDENTE'}
            </span>
          </div>
          <p className="font-rajdhani font-semibold text-sm uppercase tracking-wider text-navy">DJI CLOUD</p>
          <p className="text-gray-400 text-xs font-mono">FH2 {djiStatus?.fh2_configured ? 'OK' : 'OFF'} | SN: {djiStatus?.device_sn || '--'}</p>
        </div>
      </div>
    </div>
  );
};

export default LivePage;
